import { useCallback, useEffect, useState } from 'react';
import { IconJD } from './atoms';
import { useAuth } from '../../auth/AuthContext';
import { currentUserEmail } from '../../auth/cognito';
import {
  listCalendarConnections,
  getCalendarOAuthState,
  disconnectCalendar,
  triggerCalendarSync,
  toggleSubCalendars,
  exportAccount,
  deleteAccount,
  getPreferences,
  savePreferences,
  type CalendarConnection,
} from '../../api/client';

type ThemeChoice = 'system' | 'light' | 'dark';

export function SettingsModal({
  onClose,
  onOpenNudges,
}: {
  onClose: () => void;
  onOpenNudges: () => void;
}) {
  const { signOut } = useAuth();
  const email = currentUserEmail();

  const [theme, setTheme] = useState<ThemeChoice>(() => {
    return (document.documentElement.getAttribute('data-theme') as ThemeChoice) || 'system';
  });

  const [calConnections, setCalConnections] = useState<CalendarConnection[]>([]);
  const [calLoading, setCalLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  const [notifBriefings, setNotifBriefings] = useState(true);
  const [notifActions, setNotifActions] = useState(true);
  const [notifMorning, setNotifMorning] = useState(false);
  const [improveReplies, setImproveReplies] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setCalLoading(true);
    listCalendarConnections()
      .then((res) => setCalConnections(res.connections ?? []))
      .catch(() => {})
      .finally(() => setCalLoading(false));
    getPreferences()
      .then((p) => setImproveReplies(p.improveReplies ?? false))
      .catch(() => {});
  }, []);

  const changeTheme = useCallback((t: ThemeChoice) => {
    setTheme(t);
    if (t === 'system') {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('jotly-theme');
    } else {
      document.documentElement.setAttribute('data-theme', t);
      localStorage.setItem('jotly-theme', t);
    }
  }, []);

  const connectGoogle = useCallback(async () => {
    try {
      const { url } = await getCalendarOAuthState();
      window.open(url, '_blank', 'width=500,height=700');
    } catch { /* silently fail */ }
  }, []);

  const doDisconnect = useCallback(async (provider: 'GOOGLE' | 'APPLE', email: string) => {
    setCalConnections((prev) => prev.filter((c) => c.accountEmail !== email));
    await disconnectCalendar(provider, email).catch(() => {});
  }, []);

  const doToggleCal = useCallback(async (email: string, calId: string, enabled: boolean) => {
    setCalConnections((prev) =>
      prev.map((c) =>
        c.accountEmail === email
          ? { ...c, calendars: c.calendars.map((sc) => sc.id === calId ? { ...sc, enabled } : sc) }
          : c,
      ),
    );
    await toggleSubCalendars(email, { [calId]: enabled }).catch(() => {});
  }, []);

  const doSync = useCallback(async () => {
    setSyncing(true);
    await triggerCalendarSync().catch(() => {});
    const res = await listCalendarConnections().catch(() => ({ connections: [] }));
    setCalConnections(res.connections ?? []);
    setLastSynced('just now');
    setSyncing(false);
  }, []);

  const toggleImproveReplies = useCallback((v: boolean) => {
    setImproveReplies(v);
    savePreferences({ improveReplies: v }).catch(() => {});
  }, []);

  const doExport = useCallback(async () => {
    setExporting(true);
    try {
      const { url } = await exportAccount();
      window.open(url, '_blank');
    } catch { /* silently fail */ }
    setExporting(false);
  }, []);

  const doDelete = useCallback(async () => {
    await deleteAccount().catch(() => {});
    signOut();
  }, [signOut]);

  return (
    <div className="jd-modal-overlay" onClick={onClose}>
      <div className="jd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jd-modal-head">
          <div className="jd-modal-title">Settings</div>
          <button type="button" className="jd-icon-btn jd-modal-close" onClick={onClose}>
            <IconJD name="x" size={16} color="currentColor" stroke={2} />
          </button>
        </div>

        <div className="jd-modal-body">
          {/* ── Appearance ── */}
          <SettingsGroup label="Appearance">
            <div className="jd-settings-seg">
              {(['system', 'light', 'dark'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`jd-settings-seg-btn ${theme === t ? 'active' : ''}`}
                  onClick={() => changeTheme(t)}
                >
                  {t === 'system' ? 'Auto' : t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </SettingsGroup>

          {/* ── Capture ── */}
          <SettingsGroup label="Capture">
            <SettingsRow icon="sparkle" label="Paste a link" detail="URL unfurled and saved" />
            <SettingsRow icon="image" label="Drop a screenshot" detail="OCR reads the text" />
            <SettingsRow icon="mic" label="Voice capture" detail="Push to talk from the composer" />
          </SettingsGroup>

          {/* ── Calendar ── */}
          <SettingsGroup label="Calendar" detail="read-only">
            {calLoading ? (
              <div className="jd-settings-row"><span className="jd-settings-detail">Loading...</span></div>
            ) : calConnections.length === 0 ? (
              <button type="button" className="jd-settings-action-row" onClick={connectGoogle}>
                <IconJD name="cal" size={16} color="var(--accent)" stroke={1.6} />
                <span>Connect Google Calendar</span>
                <IconJD name="chev" size={14} color="var(--ink-3)" stroke={1.6} />
              </button>
            ) : (
              <>
                {calConnections.map((conn) => {
                  const enabled = conn.calendars.filter((sc) => sc.enabled).length;
                  const kindLabel = conn.kind
                    ? conn.kind.charAt(0).toUpperCase() + conn.kind.slice(1)
                    : 'Personal';
                  return (
                    <div key={conn.accountEmail} className="jd-settings-cal-conn">
                      <div className="jd-settings-cal-head">
                        <CalAva email={conn.accountEmail} size={24} />
                        <div className="jd-settings-cal-head-text">
                          <span className="jd-settings-cal-email">{conn.accountEmail}</span>
                          <span className="jd-settings-cal-meta">
                            {kindLabel} · {enabled} of {conn.calendars.length} synced
                          </span>
                        </div>
                        <button
                          type="button"
                          className="jd-settings-cal-disconnect"
                          onClick={() => doDisconnect(conn.provider, conn.accountEmail)}
                        >
                          Disconnect
                        </button>
                      </div>
                      {conn.calendars.length > 0 && (
                        <div className="jd-settings-cal-list">
                          {conn.calendars.map((cal) => (
                            <button
                              key={cal.id}
                              type="button"
                              className={`jd-settings-cal-item${cal.enabled ? '' : ' off'}`}
                              onClick={() => doToggleCal(conn.accountEmail, cal.id, !cal.enabled)}
                            >
                              <span
                                className="jd-settings-cal-dot"
                                style={{ background: cal.colour ?? 'var(--accent)' }}
                              />
                              <span className="jd-settings-cal-name">{cal.name}</span>
                              <span className={`jd-settings-cbx${cal.enabled ? ' on' : ''}`}>
                                {cal.enabled && (
                                  <IconJD name="check" size={12} color="#fff" stroke={2.4} />
                                )}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="jd-settings-sync-row">
                  <span className="jd-settings-detail">
                    {(() => {
                      const totalEnabled = calConnections.reduce(
                        (n, c) => n + c.calendars.filter((sc) => sc.enabled).length, 0,
                      );
                      const totalCals = calConnections.reduce(
                        (n, c) => n + c.calendars.length, 0,
                      );
                      const lastSync = calConnections
                        .map((c) => c.lastSyncedAt).filter(Boolean).sort().pop();
                      const ago = lastSynced ?? (lastSync ? relativeTime(lastSync) : 'never');
                      return `${totalEnabled} of ${totalCals} · last synced ${ago}`;
                    })()}
                  </span>
                  <button
                    type="button"
                    className="jd-settings-sync-btn"
                    onClick={doSync}
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync now'}
                  </button>
                </div>
                <button type="button" className="jd-settings-action-row" onClick={connectGoogle}>
                  <IconJD name="plus" size={14} color="var(--accent)" stroke={1.6} />
                  <span>Add Google account</span>
                </button>
              </>
            )}
          </SettingsGroup>

          {/* ── Notifications ── */}
          <SettingsGroup label="Notifications" detail="silent by default">
            <button
              type="button"
              className="jd-settings-action-row"
              onClick={() => { onClose(); onOpenNudges(); }}
            >
              <IconJD name="bell" size={16} color="var(--accent)" stroke={1.6} />
              <span>Review what I'd send<span className="jd-settings-action-desc">See every nudge, when & why — before any fire.</span></span>
              <IconJD name="chev" size={14} color="var(--ink-3)" stroke={1.6} />
            </button>
            <SettingsToggle label="Meeting briefings" detail="~10 min before, with what to bring up." checked={notifBriefings} onChange={setNotifBriefings} />
            <SettingsToggle label="Action events" detail="Pickups, bills, set reminders — last sensible moment." checked={notifActions} onChange={setNotifActions} />
            <SettingsToggle label="Quiet morning brief" detail="Optional. Off unless you want it." checked={notifMorning} onChange={setNotifMorning} />
          </SettingsGroup>

          {/* ── Data & trust ── */}
          <SettingsGroup label="Data & trust">
            <SettingsRow icon="lock" label="Encrypted at rest" detail="On" />
            <SettingsToggle
              label="Improve my replies for you"
              detail="Your corrections sharpen routing — for you only."
              checked={improveReplies}
              onChange={toggleImproveReplies}
            />
            <button
              type="button"
              className="jd-settings-action-row"
              onClick={doExport}
              disabled={exporting}
            >
              <IconJD name="export" size={16} color="var(--accent)" stroke={1.6} />
              <span>{exporting ? 'Preparing export...' : 'Export everything'}</span>
            </button>
            {!confirmDelete ? (
              <button
                type="button"
                className="jd-settings-action-row danger"
                onClick={() => setConfirmDelete(true)}
              >
                <IconJD name="trash" size={16} color="var(--danger, #b91c1c)" stroke={1.6} />
                <span>Delete everything</span>
              </button>
            ) : (
              <div className="jd-settings-confirm">
                <span>Deletion genuinely deletes — item, vector, every trace. Are you sure?</span>
                <div className="jd-settings-confirm-btns">
                  <button type="button" className="jd-settings-btn" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </button>
                  <button type="button" className="jd-settings-btn danger" onClick={doDelete}>
                    Yes, delete everything
                  </button>
                </div>
              </div>
            )}
          </SettingsGroup>

          {/* ── Account ── */}
          <SettingsGroup label="Account">
            <div className="jd-settings-row">
              <span className="jd-settings-label">{email ?? 'Signed in'}</span>
              <span className="jd-settings-detail">Magic-link sign-in</span>
            </div>
            <div className="jd-settings-actions">
              <button type="button" className="jd-settings-btn" onClick={signOut}>
                Sign out
              </button>
            </div>
          </SettingsGroup>

          <div className="jd-settings-footer">Jotly · v1 beta</div>
        </div>
      </div>
    </div>
  );
}

function SettingsGroup({
  label,
  detail,
  children,
}: {
  label: string;
  detail?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="jd-settings-group">
      <div className="jd-settings-group-label">
        {label}
        {detail && <span className="jd-settings-group-detail">{detail}</span>}
      </div>
      {children}
    </div>
  );
}

function SettingsRow({
  icon,
  label,
  detail,
}: {
  icon?: string;
  label: string;
  detail?: string;
}) {
  return (
    <div className="jd-settings-row">
      {icon && <IconJD name={icon as any} size={16} color="var(--ink-3)" stroke={1.6} />}
      <span className="jd-settings-label">{label}</span>
      {detail && <span className="jd-settings-detail">{detail}</span>}
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function CalAva({ email, size = 24 }: { email: string; size?: number }) {
  const hues: Record<string, string> = { r: '#ea580c', s: '#3b6fe0', j: '#10a37f', d: '#9b59d0', m: '#d04d6a' };
  const bg = hues[(email[0] || 'r').toLowerCase()] ?? '#3b6fe0';
  return (
    <span
      className="jd-cal-ava"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        display: 'inline-grid',
        placeItems: 'center',
        color: '#fff',
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {(email[0] || '?').toUpperCase()}
    </span>
  );
}

function SettingsToggle({
  label,
  detail,
  checked,
  onChange,
}: {
  label: string;
  detail?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="jd-settings-toggle-row"
      onClick={() => onChange(!checked)}
    >
      <div>
        <span className="jd-settings-label">{label}</span>
        {detail && <span className="jd-settings-toggle-detail">{detail}</span>}
      </div>
      <span className={`jd-nudge-switch ${checked ? 'on' : ''}`}>
        <span className="jd-nudge-knob" />
      </span>
    </button>
  );
}
